import { create } from "zustand"
import { persist } from "zustand/middleware"

import { Experiment } from "../constants/experiments"

interface ExperimentState {
  experiments: Experiment[]
  enableExperiment: (experiment: Experiment) => void
  disableExperiment: (experiment: Experiment) => void
  getEnabledExperiments: () => Experiment[]
  getAvailableExperiments: () => Experiment[]
  enableAllExperiments: () => void
  disableAllExperiments: () => void
}

const ALL_EXPERIMENTS = Object.values(Experiment) as unknown as Experiment[]

const isExperiment = (value: unknown): value is Experiment =>
  ALL_EXPERIMENTS.includes(value as Experiment)

export const useExperimentStore = create<ExperimentState>()(
  persist(
    (set, get) => ({
      experiments: [],
      enableExperiment: (experiment: Experiment) => {
        if (!ALL_EXPERIMENTS.includes(experiment)) {
          throw new Error(`${experiment} is not a valid feature.`)
        }

        set(({ experiments }) => ({
          // Prevent duplicates
          experiments: (experiments.includes(experiment)
            ? experiments
            : [...experiments, experiment]
          ).filter(
            // clear out any old experiments
            (experiment) => ALL_EXPERIMENTS.includes(experiment)
          ),
        }))
      },
      enableAllExperiments: () => set({ experiments: ALL_EXPERIMENTS }),
      disableAllExperiments: () => set({ experiments: [] }),
      disableExperiment: (experiment: Experiment) => {
        if (!ALL_EXPERIMENTS.includes(experiment)) {
          throw new Error(`${experiment} is not a valid feature.`)
        }
        set(({ experiments }) => ({
          experiments: experiments.filter((e) => e !== experiment),
        }))
      },
      getEnabledExperiments: () => get().experiments,
      getAvailableExperiments: () => ALL_EXPERIMENTS,
    }),
    {
      name: "experiments",
      partialize: (state) => ({ experiments: state.experiments }),
    }
  )
)

export const useIsExperiment = (experiment: Experiment) => {
  const experiments = useExperimentStore((state) => state.experiments)
  return experiments.includes(experiment)
}

/**
 * This should be called once from the browser entry point
 */
export const initializeExperiments = () => {
  const {
    enableExperiment,
    disableExperiment,
    getEnabledExperiments,
    getAvailableExperiments,
    disableAllExperiments,
    enableAllExperiments,
  } = useExperimentStore.getState()

  const searchParams = new URLSearchParams(globalThis.location.search)
  const testExperiments = searchParams.get("testExperiments")

  /**
   * NOTE: searh params should be used to test in staging
   * and will more than likely cause unpredictable behaviour
   * in production due to race conditions with vwo script
   */

  if (testExperiments) {
    if (testExperiments === "all") {
      enableAllExperiments()
    } else if (testExperiments === "none") {
      disableAllExperiments()
    } else {
      const experiments = testExperiments.split(",")
      // Clear out any existing experiments
      disableAllExperiments()
      for (const experiment of experiments) {
        if (isExperiment(experiment)) {
          enableExperiment(experiment)
        }
      }
    }
  }

  window.experiments = {
    enableExperiment,
    disableExperiment,
    getEnabledExperiments,
    getAvailableExperiments,
    disableAllExperiments,
    enableAllExperiments,
    /**
     * ToDo: remove these once VWO confirms they have switched to the new API above
     */
    getExperiments: getEnabledExperiments,
    addExperiment: enableExperiment,
    removeExperiment: disableExperiment,
  }

  const experimentsInitializedEvent = new CustomEvent("experimentsInitialized")
  globalThis.dispatchEvent(experimentsInitializedEvent)
}
